/*********************************************************************************
 * Copyright (C) 2025 VECROS Technologies Pvt. Ltd.
 * <legal@vecros.com>
 * This file is part of VECROS CLOUD SOFTWARE SERVICES maintained and developed by VECROS TECHNOLOGIES PRIVATE LIMITED.
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 **********************************************************************************/

import { useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import { formatBytes, formatDate } from "../format";

const DriveTable = ({
  folders,
  files,
  selected,
  favorites,
  onToggleFavorite,
  onToggleSelect,
  onToggleSelectAll,
  onOpenFolder,
  onPreview,
  onDownload,
  onVersionHistory,
  onComments,
  onLabels,
  onRename,
  onMove,
  onShare,
  onDelete,
}) => {
  const [menu, setMenu] = useState(null); // {anchor, item}

  const openMenu = (event, item) => {
    event.stopPropagation();
    setMenu({ anchor: event.currentTarget, item });
  };
  const closeMenu = () => setMenu(null);

  const run = (action) => () => {
    const { item } = menu;
    closeMenu();
    action(item);
  };

  const rows = [
    ...folders.map((f) => ({
      id: f.id,
      type: "folder",
      name: f.folder_name,
      size: null,
      updatedAt: f.updated_at,
      ownerId: f.user_id,
      teamId: f.team_id,
    })),
    ...files.map((f) => ({
      id: f.id,
      type: "file",
      name: f.file_name,
      size: f.size_bytes,
      updatedAt: f.updated_at,
      ownerId: f.user_id,
    })),
  ];

  if (rows.length === 0) {
    return (
      <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
        <Typography variant="body1">This folder is empty.</Typography>
        <Typography variant="body2">Drop files here or use the Upload button.</Typography>
      </Box>
    );
  }

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const someSelected = rows.some((r) => selected.has(r.id)) && !allSelected;

  return (
    <>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => onToggleSelectAll(rows)}
                inputProps={{ "aria-label": "Select all" }}
                sx={{ "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "accent.main" } }}
              />
            </TableCell>
            <TableCell>Name</TableCell>
            <TableCell padding="checkbox" />
            <TableCell width={120}>Size</TableCell>
            <TableCell width={140}>Modified</TableCell>
            <TableCell width={56} />
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow
              key={`${row.type}-${row.id}`}
              hover
              selected={selected.has(row.id)}
              sx={{ cursor: row.type === "folder" ? "pointer" : "default" }}
              onDoubleClick={() =>
                row.type === "folder" ? onOpenFolder(row.id) : onPreview(row)
              }
            >
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={selected.has(row.id)}
                  onChange={() => onToggleSelect(row)}
                  inputProps={{ "aria-label": `Select ${row.name}` }}
                  sx={{ "&.Mui-checked": { color: "accent.main" } }}
                />
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {row.type === "folder" ? (
                    <FolderIcon fontSize="small" sx={{ color: "accent.main" }} />
                  ) : (
                    <InsertDriveFileIcon fontSize="small" sx={{ color: "text.color", opacity: 0.6 }} />
                  )}
                  {/* Two lines, as WorkDrive does: name over provenance. */}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      onClick={() => (row.type === "folder" ? onOpenFolder(row.id) : onPreview(row))}
                      sx={{ cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                    >
                      {row.name}
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "secondaryText.color" }} noWrap>
                      {row.teamId ? "Team folder" : "Uploaded by you"}
                    </Typography>
                  </Box>
                </Box>
              </TableCell>
              <TableCell padding="checkbox">
                <IconButton
                  size="small"
                  onClick={() => onToggleFavorite(row)}
                  aria-label={
                    favorites?.has(row.id) ? `Unstar ${row.name}` : `Star ${row.name}`
                  }
                >
                  {favorites?.has(row.id) ? (
                    <StarIcon fontSize="small" sx={{ color: "#F2B01E" }} />
                  ) : (
                    <StarBorderIcon fontSize="small" sx={{ color: "text.color", opacity: 0.35 }} />
                  )}
                </IconButton>
              </TableCell>
              <TableCell>{row.type === "folder" ? "—" : formatBytes(row.size)}</TableCell>
              <TableCell>{formatDate(row.updatedAt)}</TableCell>
              <TableCell>
                <IconButton size="small" onClick={(e) => openMenu(e, row)} aria-label={`Actions for ${row.name}`}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Menu anchorEl={menu?.anchor} open={Boolean(menu)} onClose={closeMenu}>
        {menu?.item.type === "file" && <MenuItem onClick={run(onPreview)}>Preview</MenuItem>}
        {menu?.item.type === "file" && <MenuItem onClick={run(onDownload)}>Download</MenuItem>}
        {menu?.item.type === "file" && <MenuItem onClick={run(onVersionHistory)}>Version history</MenuItem>}
        {menu?.item.type === "file" && <MenuItem onClick={run(onComments)}>Comments</MenuItem>}
        {menu?.item.type === "file" && <MenuItem onClick={run(onLabels)}>Labels</MenuItem>}
        <MenuItem onClick={run(onRename)}>Rename</MenuItem>
        <MenuItem onClick={run(onMove)}>Move to…</MenuItem>
        <MenuItem onClick={run(onShare)}>Share</MenuItem>
        <MenuItem onClick={run(onDelete)} sx={{ color: "error.main" }}>Delete</MenuItem>
      </Menu>
    </>
  );
};

export default DriveTable;
